/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useI18n } from "hooks/i18n/i18n";
import { modalActions } from "modules/modal";
import { purchaseActions } from "modules/purchase";
import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { getCardTokenGMO } from "utility/gmo";
import { z } from "zod/v3";

type CardInputInfo = {
  cardNumber: string;
  cardExpire: {
    month: string;
    year: string;
  };
  cardHolderName: string;
  cardSecurityCode: string;
};

const registerCardInfoSchema = z.object({
  cardNumber: z
    .string()
    .transform((val) => val.replace(/[\s-ー－]/g, ""))
    .pipe(z.string().regex(/^\d{14,16}$/, "カード番号が正しくありません")),
  cardExpire: z
    .object({
      month: z
        .string()
        .transform((val) => val.padStart(2, "0"))
        .pipe(
          z.string().regex(/^(0[1-9]|1[0-2])$/, "有効期限が正しくありません")
        ),
      year: z.string().regex(/^([0-9]{2})$/, "有効期限が正しくありません"),
    })
    .superRefine((data, ctx) => {
      const now = new Date();
      const currentYear = now.getFullYear() % 100;
      const currentMonth = now.getMonth() + 1;

      const expYear = parseInt(data.year, 10);
      const expMonth = parseInt(data.month, 10);

      const isExpired =
        expYear < currentYear ||
        (expYear === currentYear && expMonth < currentMonth);

      if (isExpired) {
        const errorMessage = "有効期限が切れています";
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: errorMessage,
          path: ["month"],
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: errorMessage,
          path: ["year"],
        });
      }
    }),
  cardHolderName: z
    .string()
    .min(1, "カード名義人を入力してください")
    .transform((val) => val.toUpperCase())
    .refine((val) => /^[A-Z\s]+$/.test(val), "半角英字で入力してください"),
  cardSecurityCode: z
    .string()
    .regex(/^\d{3,4}$/, "セキュリティコードの形式が正しくありません"),
});

type RegisterCardInfo = z.infer<typeof registerCardInfoSchema>;

export const StreamingLiveCommerceRegisterCreditV2: React.FC<{
  isDarkModeEnabled: boolean;
  setSelectedCardType: (type: "registered" | "new") => void;
}> = ({ isDarkModeEnabled, setSelectedCardType }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegisterCardInfo>({
    resolver: zodResolver(registerCardInfoSchema),
    mode: "onChange",
  });

  const { t } = useI18n();
  const classesCreditPaymentDesign = registerCreditPaymentDesign({
    isDarkModeEnabled,
    isValid,
  });

  const dispatch = useDispatch();
  const handleRegisterNewCard = async (registerCardInfo: CardInputInfo) => {
    try {
      const response = await getCardTokenGMO({
        cardNo: registerCardInfo.cardNumber,
        expire:
          registerCardInfo.cardExpire.year.toString() +
          registerCardInfo.cardExpire.month.toString(),
        securityCode: registerCardInfo.cardSecurityCode ?? "",
        holderName: registerCardInfo.cardHolderName,
        tokenNumber: 1,
      });

      if (!response.tokens || response.tokens.length === 0) {
        dispatch(modalActions.toggleError({ msg: "カード登録に失敗しました" }));
        return;
      }

      dispatch(
        purchaseActions.registerCardInfo.started({ token: response.tokens[0] })
      );
      setSelectedCardType("registered");
    } catch (error) {
      dispatch(
        modalActions.toggleError({ msg: "システムエラーが発生しました" })
      );
    }
  };

  return (
    <div css={classesCreditPaymentDesign.formContainer}>
      <div css={classesCreditPaymentDesign.inputGroup}>
        <label>{t("streaming.commerceCart.cardNumber")}</label>
        <input
          type="text"
          placeholder="1234123412341234"
          css={classesCreditPaymentDesign.textInput}
          {...register("cardNumber")}
          inputMode="numeric"
        />
        {errors.cardNumber && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardNumber.message}
          </p>
        )}
        <div css={classesCreditPaymentDesign.brandImageWrapper}>
          <img
            src="/images/liveCommerce/paymentMethodCard.png"
            alt="Card Brands"
            css={classesCreditPaymentDesign.brandImage}
          />
        </div>
      </div>

      <div css={classesCreditPaymentDesign.inputGroup}>
        <label>{t("streaming.commerceCart.cardExpiration")}</label>
        <div css={classesCreditPaymentDesign.flexRow}>
          <input
            type="text"
            placeholder="MM"
            css={classesCreditPaymentDesign.smallInput}
            {...register("cardExpire.month")}
          />
          <input
            type="text"
            placeholder="YY"
            css={classesCreditPaymentDesign.smallInput}
            {...register("cardExpire.year")}
          />
        </div>
        {errors.cardExpire && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardExpire?.month?.message ??
              errors.cardExpire?.year?.message ??
              errors.cardExpire?.message}
          </p>
        )}
      </div>

      <div css={classesCreditPaymentDesign.inputGroup}>
        <label>{t("streaming.commerceCart.cardHolderName")}</label>
        <input
          type="text"
          placeholder="TARO YAMADA"
          css={classesCreditPaymentDesign.textInput}
          {...register("cardHolderName")}
        />
        {errors.cardHolderName && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardHolderName.message}
          </p>
        )}
      </div>

      <div css={classesCreditPaymentDesign.inputGroup}>
        <label>{t("streaming.commerceCart.cardCVV")}</label>
        <input
          type="text"
          placeholder="123"
          css={[
            classesCreditPaymentDesign.textInput,
            css`
              width: 80px;
            `,
          ]}
          {...register("cardSecurityCode")}
        />
        {errors.cardSecurityCode && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardSecurityCode.message}
          </p>
        )}
      </div>
      <div
        onClick={handleSubmit(handleRegisterNewCard)}
        css={classesCreditPaymentDesign.registerCardButton}
      >
        {t("streaming.commerceCreditRegister.registerCard")}
      </div>
    </div>
  );
};

type RegisterCreditStylesProps = {
  isDarkModeEnabled: boolean;
  isValid: boolean;
};

const registerCreditPaymentDesign = (props: RegisterCreditStylesProps) => {
  const theme = {
    text: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
    bg: props.isDarkModeEnabled ? "#000000" : "#FFFFFF",
    inputBg: props.isDarkModeEnabled ? "#1A1A1A" : "#F0F0F0",
    border: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
  };
  const formContainer = css`
    margin-top: 20px;
    padding-left: 20px;
  `;
  const inputGroup = css`
    margin-bottom: 16px;
    label {
      display: block;
      font-size: 14px;
      margin-bottom: 8px;
      color: ${theme.text};
    }
  `;
  const textInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    border: none;
    color: ${theme.text};
    font-size: 16px;
    &::placeholder {
      color: #a3a3a3;
    }
  `;

  const flexRow = css`
    display: flex;
    gap: 12px;
  `;
  const smallInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    font-size: 16px;
    width: 70px;
    color: ${theme.text};
    &::placeholder {
      color: #a3a3a3;
    }
  `;
  const brandImageWrapper = css`
    margin-top: 8px;
  `;
  const brandImage = css`
    height: 24px;
    object-fit: contain;
  `;
  const textColor = props.isValid ? "#000000" : "#5C5C5C";
  const backgroundColor = props.isValid ? "#FFFFFF" : "#a3a3a3";
  const cursorStyle = props.isValid ? "pointer" : "not-allowed";
  const registerCardButton = css`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${textColor};
    text-align: center;
    margin-top: 16px;
    background-color: ${backgroundColor};
    cursor: ${cursorStyle};
    font-size: 14px;
    font-weight: 600;
    width: fit-content;
    height: 32px;
    border-radius: 6px;
    padding: 0 12px;
  `;

  const errorText = css`
    color: red;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
  `;

  return {
    formContainer,
    inputGroup,
    textInput,
    smallInput,
    flexRow,
    brandImageWrapper,
    brandImage,
    registerCardButton,
    errorText,
  };
};
